import { useState } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { Button } from '@monksflow/monks-ui';
import { CheckIcon, CopyIcon, RefreshCwIcon } from 'lucide-react';
import { InfoCard } from '@/entities/widget';
import { MessageMetadata } from '@/entities/ai';
import { useRetryWithProvider } from '@/features/chats/model/useRetryWithProvider';
import { getProviderDisplayName } from '../ChatOptions/StrategyAgent/StrategyAgentOptions.types';
import { useTranslation } from 'react-i18next';

interface ErrorMetadataProps {
  issue: MessageMetadata['issue'];
}

export const ErrorMetadata = ({ issue }: ErrorMetadataProps) => {
  const { t } = useTranslation('chats');

  const [copied, setCopied] = useState(false);
  const { retry } = useRetryWithProvider();
  const retryUiEnabled = useBooleanFlagValue('provider_fallback_retry', false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(issue?.traceId ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const alternatives = retryUiEnabled && issue && 'alternatives' in issue ? issue.alternatives : [];

  return (
    <InfoCard style="mt-2 max-w-[350px]">
      {alternatives.length > 0 && (
        <div className="mb-4 flex flex-row flex-wrap gap-2">
          {alternatives.map((alt) => (
            <Button
              key={alt.provider}
              type="button"
              size="sm"
              onClick={() => void retry({ provider: alt.provider, modelKey: alt.modelKey })}
            >
              <RefreshCwIcon />
              {t('chats.errorMetadata.retryWithProvider', {
                provider: getProviderDisplayName(alt.provider),
              })}
            </Button>
          ))}
        </div>
      )}

      <p className="mb-2 text-xs leading-4 text-muted-foreground">
        {t('chats.errorMetadata.pleaseProvideThisIdentifierTo')}
      </p>
      <Button
        type="button"
        className="justify-start text-left"
        size="sm"
        variant="outline"
        onClick={() => void handleCopy()}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
        {issue?.traceId}
      </Button>
    </InfoCard>
  );
};
